/**
 * Save/load slot picker (auto + manual-1) showing chapter beat, playtime and timestamp.
 * Used by the title's 继续旅程 and the pause menu's 保存/读取.
 */
import Phaser from 'phaser';
import { GAME_H, GAME_W } from '@/core/config';
import { audio } from '@/audio/index';
import { SAVE_SLOTS, type SaveSlot } from '@/save/schema';
import { describeSlot, type SlotSummary } from '@/save/labels';
import type { UiRoot } from './UiRoot';
import { uiText } from './text';
import { Css, Slices, UiDepth } from './theme';

export type SlotMode = 'save' | 'load';
export interface SlotPanelOptions { standalone?: boolean; depth?: number; y?: number }

interface Row { zone: Phaser.GameObjects.Zone; name: Phaser.GameObjects.Text; beat: Phaser.GameObjects.Text; detail: Phaser.GameObjects.Text; summary: SlotSummary; disabled: boolean }

const W = 420, H = 190;

export class SlotPanel {
  private view: { container: Phaser.GameObjects.Container; rows: Row[]; bar: Phaser.GameObjects.NineSlice; dispose: () => void } | null = null;
  private index = 0;
  private resolve: ((slot: SaveSlot | null) => void) | null = null;

  constructor(private readonly root: UiRoot) {
    root.track(() => this.cancel());
  }

  isOpen(): boolean { return this.view !== null; }

  /** Resolves with the chosen slot, or null on cancel. */
  open(mode: SlotMode, opts: SlotPanelOptions = {}): Promise<SaveSlot | null> {
    this.cancel();
    const s = this.root.scene;
    const ctx = this.root.ctx;
    const x = (GAME_W - W) / 2;
    const y = opts.y ?? (GAME_H - H) / 2 - 8;
    const container = s.add.container(0, 0).setDepth(opts.depth ?? UiDepth.PAUSE + 5);
    if (opts.standalone !== false) container.add(s.add.rectangle(0, 0, GAME_W, GAME_H, 0x000000, 0.55).setOrigin(0).setInteractive());
    container.add(s.add.nineslice(x, y, Slices.scroll.key, undefined, W, H, Slices.scroll.l, Slices.scroll.r, Slices.scroll.t, Slices.scroll.b).setOrigin(0));
    container.add(uiText(s, x + W / 2, y + 18, mode === 'save' ? '保存进度' : '读取进度', { fontSize: '15px', fontStyle: 'bold', color: Css.lacquer }).setOrigin(0.5));
    container.add(uiText(s, x + W / 2, y + H - 14, mode === 'save' ? '↑↓ 选择 · Enter 保存 · Esc 返回' : '↑↓ 选择 · Enter 读取 · Esc 返回', { fontSize: '11px', color: Css.inkSoft }).setOrigin(0.5));
    const rowH = 56;
    const bar = s.add.nineslice(x + W / 2, 0, Slices.bar.key, undefined, W - 32, rowH - 6, Slices.bar.l, Slices.bar.r, Slices.bar.t, Slices.bar.b).setOrigin(0.5);
    container.add(bar);
    const rows: Row[] = SAVE_SLOTS.map((slot, i) => {
      const data = ctx?.saves.read(slot) ?? null;
      const summary = describeSlot(slot, data);
      const disabled = mode === 'save' ? slot === 'auto' : summary.empty;
      const ry = y + 40 + i * rowH + rowH / 2;
      const zone = s.add.zone(x + W / 2, ry, W - 32, rowH - 6).setOrigin(0.5).setInteractive({ useHandCursor: !disabled });
      const name = uiText(s, x + 30, ry, summary.name, { fontSize: '14px', fontStyle: 'bold', color: Css.ink }).setOrigin(0, 0.5);
      const beat = uiText(s, x + 140, ry - 10, summary.empty ? summary.beat : `${summary.beat}`, { fontSize: '15px', color: Css.ink }).setOrigin(0, 0.5);
      const detail = uiText(s, x + 140, ry + 12, summary.detail, { fontSize: '11px', color: Css.inkSoft }).setOrigin(0, 0.5);
      zone.on('pointerover', () => { if (!disabled) this.select(i); });
      zone.on('pointerdown', () => { if (disabled) return; this.select(i); this.confirm(); });
      container.add([zone, name, beat, detail]);
      return { zone, name, beat, detail, summary, disabled };
    });
    const kb = s.input.keyboard;
    const keys: [string, () => void][] = [
      ['keydown-UP', () => this.move(-1)], ['keydown-W', () => this.move(-1)], ['keydown-DOWN', () => this.move(1)], ['keydown-S', () => this.move(1)],
      ['keydown-ENTER', () => this.confirm()], ['keydown-SPACE', () => this.confirm()], ['keydown-Z', () => this.confirm()],
      ['keydown-ESC', () => this.cancel()], ['keydown-X', () => this.cancel()], ['keydown-BACKSPACE', () => this.cancel()],
    ];
    for (const [k, fn] of keys) kb?.on(k, fn);
    const dispose = () => { for (const [k, fn] of keys) kb?.off(k, fn); container.destroy(true); };
    this.view = { container, rows, bar, dispose };
    this.root.pushModal('slots');
    audio().sfx('ui-open');
    const first = rows.findIndex((r) => !r.disabled);
    this.select(first >= 0 ? first : 0);
    return new Promise((resolve) => { this.resolve = resolve; });
  }

  private select(i: number): void {
    if (!this.view) return;
    const n = this.view.rows.length;
    this.index = ((i % n) + n) % n;
    const row = this.view.rows[this.index];
    if (!row) return;
    this.view.bar.setY(row.zone.y).setVisible(!row.disabled);
    this.view.rows.forEach((r, k) => {
      const sel = k === this.index && !r.disabled;
      const c = r.disabled ? Css.dim : sel ? Css.parchment : Css.ink;
      r.name.setColor(c); r.beat.setColor(c); r.detail.setColor(r.disabled ? Css.dim : sel ? Css.parchmentDark : Css.inkSoft);
    });
  }

  private move(dir: 1 | -1): void {
    if (!this.view) return;
    const n = this.view.rows.length;
    for (let k = 1; k <= n; k++) {
      const i = (this.index + dir * k + n) % n;
      if (!this.view.rows[i]?.disabled) { audio().sfx('ui-move'); this.select(i); return; }
    }
  }

  private confirm(): void {
    const row = this.view?.rows[this.index];
    if (!row || row.disabled) return;
    audio().sfx('ui-confirm');
    this.finish(row.summary.slot);
  }

  cancel(silent = false): void { if (this.view) { if (!silent) audio().sfx('ui-cancel'); this.finish(null); } }

  private finish(result: SaveSlot | null): void {
    const v = this.view;
    const r = this.resolve;
    this.view = null; this.resolve = null;
    if (v) { v.dispose(); this.root.popModal('slots'); }
    r?.(result);
  }
}
